import React, { useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';

import { CustomNextArrow, CustomPrevArrow } from './CustomSliderArrows';

interface MultiImageProps {
  title: string;
  description: string;
  images: {
    src: string;
    alt?: string;
  }[];
}

export const MultiImage: React.FC<MultiImageProps> = ({
  title,
  description,
  images,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 834);
      setIsTablet(window.innerWidth < 1201);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sliderSettings = useMemo(() => {
    return {
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: !isTablet,
      prevArrow: <CustomPrevArrow />,
      nextArrow: <CustomNextArrow />,
      beforeChange: (_current: number, next: number) => setCurrentSlide(next),
    };
  }, [isTablet]);

  const handleImageSelect = (index: number) => {
    setCurrentSlide(index);
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  const hasThreeImages = images.length === 3;

  const tabletThreeImagesStyle =
    isTablet && hasThreeImages
      ? {
          justifyContent: 'center',
        }
      : {};

  const tabletSmallImageStyle =
    isTablet && hasThreeImages
      ? {
          flex: '0 0 calc(33.33% - 20px)',
        }
      : {};

  const desktopThreeImagesStyle = hasThreeImages
    ? { justifyContent: 'flex-start', width: '75%' }
    : {};

  return (
    <section className="auvsi-computer-section">
      {isTablet && (
        <div
          className="auvsi-computer-images-container"
          style={{
            ...tabletThreeImagesStyle,
          }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className={`auvsi-computer-small-image-container ${
                currentSlide === index
                  ? 'auvsi-computer-small-image-selected'
                  : 'auvsi-computer-small-image-not-selected'
              }`}
              onClick={() => handleImageSelect(index)}
              style={{
                ...tabletSmallImageStyle,
              }}
            >
              <img
                src={image.src}
                alt={image.alt || `Image ${index + 1}`}
                className="auvsi-computer-small-image"
              />
            </div>
          ))}
        </div>
      )}

      <div className="auvsi-computer-main-image-container">
        <div className="auvsi-bg-gradient" />
        <div className="auvsi-bg-noise" />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 745 693"
          preserveAspectRatio="none"
          fill="none"
          className="auvsi-computer-main-image-lines"
        >
          <path
            d="M6 687H49V693H0V644H6V687ZM745 693H696V687H739V644H745V693ZM49 6H6V49H0V0H49V6ZM745 49H739V6H696V0H745V49Z"
            fill="white"
            fillOpacity="0.4"
            style={{ mixBlendMode: 'overlay' }}
          />
        </svg>

        <div className="auvsi-computer-main-image-box">
          {isMobile ? (
            <Slider ref={sliderRef} {...sliderSettings}>
              {images.map((image, index) => (
                <div key={index} className="auvsi-slide-wrapper">
                  <img
                    src={image.src}
                    alt={image.alt || `Image ${index + 1}`}
                    className="auvsi-computer-main-image"
                  />
                </div>
              ))}
            </Slider>
          ) : (
            <img
              src={images[currentSlide].src}
              alt={images[currentSlide].alt || `Image ${currentSlide + 1}`}
              className="auvsi-computer-main-image"
            />
          )}
        </div>
      </div>

      <div className="auvsi-computer-right-section">
        <div className="auvsi-computer-text-container">
          <div className="auvsi-computer-titles-section">
            <h2 className="auvsi-computer-title">{title}</h2>
            <p className="auvsi-computer-description">{description}</p>
          </div>
        </div>

        {!isTablet && (
          <div
            className="auvsi-computer-images-container"
            style={{
              ...desktopThreeImagesStyle,
            }}
          >
            {images.map((image, index) => (
              <div
                key={index}
                className={`auvsi-computer-small-image-container ${
                  currentSlide === index
                    ? 'auvsi-computer-small-image-selected'
                    : 'auvsi-computer-small-image-not-selected'
                }`}
                onClick={() => handleImageSelect(index)}
                style={
                  hasThreeImages
                    ? {
                        flex: '0 0 calc(33.33% - 20px)',
                      }
                    : {}
                }
              >
                <img
                  src={image.src}
                  alt={image.alt || `Image ${index + 1}`}
                  className="auvsi-computer-small-image"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
